// src/components/Drive/DeliveryList/DeliveryCard.jsx
import React, { useState, useMemo } from "react";
import {
  Box, Typography, Chip, Paper, IconButton,
  Collapse, Button, CircularProgress, Tooltip, Divider,
} from "@mui/material";
import {
  ArrowRight, Share2, CircleCheck, Clock,
  ChevronDown, ChevronUp, Timer, MapPin, LocateFixed,
  Car, Truck, ArrowLeftRight, Layers,
} from "lucide-react";
import { Star } from "lucide-react";

import { submitDeliveryReviewFn } from "../../../firebase/deliveryFunctions";
import {
  toDateMaybe, formatFi, formatDistance, formatDuration,
  formatTimeLeft, haversineMeters, getOriginCoords,
  URGENCY_BORDER, URGENCY_CHIP,
} from "./utils";
import { createDeliveryShare, getShareUrl } from "../../../utils/deliveryShare";

// ─── Transport type config ────────────────────────────────────────────────────

const TRANSPORT_CONFIG = {
  car:  { Icon: Car,           label: "Henkilöauto"  },
  van:  { Icon: Truck,         label: "Pakettiauto"  },
  wait: { Icon: ArrowLeftRight, label: "Bussinvaihto" },
};

const DEFAULT_TRANSPORT = { Icon: Car, label: "Henkilöauto" };

/**
 * DeliveryTypeBadge — shows transport type icon + label,
 * with a stacked-layers overlay when deliveryType === 'multi-delivery'.
 */
function DeliveryTypeBadge({ deliveryType, deliveryTransportType }) {
  const isMulti = deliveryType === "multi-delivery";
  const { Icon, label } = TRANSPORT_CONFIG[deliveryTransportType] ?? DEFAULT_TRANSPORT;

  return (
    <Tooltip title={isMulti ? `${label} · Useita kohteita` : label}>
      <Box
        sx={{
          position: "relative",
          display: "inline-flex",
          alignItems: "center",
          gap: 0.5,
          px: 0.75,
          py: 0.35,
          borderRadius: 1,
          bgcolor: "action.hover",
          border: "1px solid",
          borderColor: "divider",
          cursor: "default",
          userSelect: "none",
        }}
      >
        {/* Main transport icon */}
        <Icon size={13} style={{ opacity: 0.7, flexShrink: 0 }} />

        <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ lineHeight: 1 }}>
          {label}
        </Typography>

        {/* Multi-delivery overlay badge */}
        {isMulti && (
          <Box
            sx={{
              position: "absolute",
              top: -5,
              right: -5,
              width: 14,
              height: 14,
              borderRadius: "50%",
              bgcolor: "primary.main",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              border: "1.5px solid",
              borderColor: "background.paper",
            }}
          >
            <Layers size={8} color="white" />
          </Box>
        )}
      </Box>
    </Tooltip>
  );
}

// ─── ShareButton ──────────────────────────────────────────────────────────────

function ShareButton({ delivery }) {
  const [sharing, setSharing] = useState(false);
  const id = delivery?.id || delivery?.deliveryId;

  const handleShare = async (e) => {
    e.stopPropagation();
    setSharing(true);
    try {
      let shareUrl;
      if (delivery?.status === "active") {
        const shareId = await createDeliveryShare(id, delivery);
        shareUrl = getShareUrl(shareId);
      } else {
        shareUrl = `${window.location.origin}/vastaanota?d=${id}`;
      }
      await navigator.clipboard.writeText(shareUrl);
      alert(`Linkki kopioitu!\n${shareUrl}`);
    } catch (err) {
      console.error(err);
      alert("Virhe luotaessa jakolinkkiä");
    } finally {
      setSharing(false);
    }
  };

  return (
    <Tooltip title="Kopioi jakolinkki">
      <IconButton size="small" onClick={handleShare} disabled={sharing}>
        {sharing ? <CircularProgress size={14} /> : <Share2 size={15} />}
      </IconButton>
    </Tooltip>
  );
}

// ─── TimeRow ──────────────────────────────────────────────────────────────────

function TimeRow({ label, from, to }) {
  if (!from) return null;
  return (
    <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.75 }}>
      <Typography variant="caption" color="text.secondary" fontWeight={700}
        sx={{ minWidth: 58, flexShrink: 0 }}>
        {label}
      </Typography>
      <Typography variant="caption" color="text.primary">
        {formatFi(from)}
        {to && <Box component="span" sx={{ color: "text.secondary" }}>{" – "}{formatFi(to)}</Box>}
      </Typography>
    </Box>
  );
}

// ─── StarRow (already reviewed) ───────────────────────────────────────────────

function StarRow({ stars }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
      {[1, 2, 3, 4, 5].map((n) => (
        <Star
          key={n}
          size={14}
          fill={n <= stars ? "currentColor" : "none"}
          style={{ color: n <= stars ? "#f59e0b" : "#ccc" }}
        />
      ))}
      <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>Arvioitu</Typography>
    </Box>
  );
}

// ─── QuickReview ──────────────────────────────────────────────────────────────

function QuickReview({ delivery, onReview }) {
  const [stars, setStars]       = useState(5);
  const [hover, setHover]       = useState(0);
  const [submitting, setSubmitting] = useState(false);

  const handleAllGood = async (e) => {
    e.stopPropagation();
    setSubmitting(true);
    try {
      await submitDeliveryReviewFn(delivery.id ?? delivery.deliveryId, {
        stars,
        checks: { origin: true, destination: true, times: true, reward: true },
        notes: {},
        dispute: false,
      });
    } catch (err) {
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
      <Box sx={{ display: "flex", gap: 0.5 }}>
        {[1, 2, 3, 4, 5].map((n) => (
          <Box
            key={n}
            onClick={(e) => { e.stopPropagation(); setStars(n); }}
            onMouseEnter={() => setHover(n)}
            onMouseLeave={() => setHover(0)}
            sx={{
              cursor: "pointer", display: "flex",
              color: n <= (hover || stars) ? "warning.main" : "action.disabled",
              transition: "color 0.1s",
            }}
          >
            <Star size={26} fill={n <= (hover || stars) ? "currentColor" : "none"} />
          </Box>
        ))}
      </Box>
      <Box sx={{ display: "flex", gap: 1, width: "100%" }}>
        <Button
          size="small"
          variant="outlined"
          color="error"
          disabled={submitting}
          onClick={(e) => { e.stopPropagation(); onReview(); }}
          sx={{ fontWeight: 600, fontSize: "0.72rem", py: 0.6, textTransform: "none", flexShrink: 0 }}
        >
          Ilmoita ongelma
        </Button>
        <Button
          size="small"
          variant="contained"
          disableElevation
          disabled={submitting}
          onClick={handleAllGood}
          sx={{ flex: 1, fontWeight: 700, fontSize: "0.75rem", py: 0.6, textTransform: "none" }}
        >
          {submitting ? <CircularProgress size={14} color="inherit" /> : "Anna arvio"}
        </Button>
      </Box>
    </Box>
  );
}

// ─── DeliveryCard ─────────────────────────────────────────────────────────────

export function DeliveryCard({ delivery, courierCoords, onAccept, onOpen, onReview }) {
  const isActive    = delivery?.status === "active";
  const isOngoing   = delivery?.status === "ongoing" || delivery?.status === "picked";
  const isCompleted = delivery?.status === "completed";

  const [expanded, setExpanded] = useState(false);

  const pickUpDate         = toDateMaybe(delivery?.pickupTime        ?? delivery?.pickUpTime);
  const latestPickUpDate   = toDateMaybe(delivery?.latestPickUpTime  ?? null);
  const earliestTargetDate = toDateMaybe(delivery?.earliestTargetTime ?? null);
  const targetDate         = toDateMaybe(delivery?.targetTime);
  const completedDate      = toDateMaybe(delivery?.completedAtMs     ?? delivery?.completedAt);

  const origin      = delivery?.originInfo?.address      || delivery?.origin      || "—";
  const destination = delivery?.destinationInfo?.address || delivery?.destination || "—";

  const distance = formatDistance(delivery?.distance);
  const duration = formatDuration(delivery?.duration);
  const reward   = delivery?.reward;

  const { label: timeLeftLabel, urgency } = isCompleted
    ? { label: null, urgency: "later" }
    : formatTimeLeft(pickUpDate);

  const chipStyle   = URGENCY_CHIP[urgency];
  const borderColor = URGENCY_BORDER[urgency];

  const hasWindow  = !!delivery?.hasTimeWindow;
  const pickupTo   = hasWindow ? latestPickUpDate   : null;
  const targetFrom = hasWindow ? earliestTargetDate : null;

  const proximityM = useMemo(() => {
    if (!courierCoords) return null;
    const orig = getOriginCoords(delivery);
    if (!orig) return null;
    return haversineMeters(courierCoords, orig);
  }, [courierCoords, delivery]);

  const proximityLabel = proximityM != null ? formatDistance(proximityM) : null;

  const cardClick = isActive ? onAccept : isOngoing ? onOpen : undefined;

  return (
    <Paper
      variant="outlined"
      sx={{
        borderRadius: 2,
        overflow: "hidden",
        borderColor,
        borderWidth: urgency !== "later" ? 1.5 : 1,
        transition: "box-shadow 0.15s",
        "&:hover": { boxShadow: 2 },
      }}
    >
      {/* ── Route + reward ── */}
      <Box
        onClick={cardClick}
        sx={{
          cursor: cardClick ? "pointer" : "default",
          p: { xs: 1.5, sm: 2 },
          display: "grid",
          gridTemplateColumns: "1fr auto",
          gap: 2,
          alignItems: "start",
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, overflow: "hidden" }}>
            <Typography variant="body1" fontWeight={700} noWrap sx={{ maxWidth: { xs: 130, sm: 260 } }}>
              {origin}
            </Typography>
            <ArrowRight size={14} style={{ opacity: 0.35, flexShrink: 0 }} />
            <Typography variant="body1" fontWeight={700} noWrap sx={{ maxWidth: { xs: 130, sm: 260 } }}>
              {destination}
            </Typography>
          </Box>
          {(delivery?.returnParcels || delivery?.temperatureControl || delivery?.canBeLeftAtDoor) && (
            <Box sx={{ display: "flex", gap: 0.5, mt: 0.5, flexWrap: "wrap" }}>
              {delivery.returnParcels      && <Chip label="Palautus" size="small" variant="outlined" sx={{ height: 20, fontSize: "0.68rem" }} />}
              {delivery.temperatureControl && <Chip label="Lämpö"    size="small" variant="outlined" sx={{ height: 20, fontSize: "0.68rem" }} />}
              {delivery.canBeLeftAtDoor    && <Chip label="Ovelle"   size="small" variant="outlined" sx={{ height: 20, fontSize: "0.68rem" }} />}
            </Box>
          )}
        </Box>

        <Box sx={{ textAlign: "right", flexShrink: 0 }}>
          {reward != null && (
            <Typography variant="h5" fontWeight={900} color="primary.main" sx={{ lineHeight: 1 }}>
              {reward} €
            </Typography>
          )}
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 0.2, mt: 0.4 }}>
            {(distance || duration) && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                {distance && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.3 }}>
                    <MapPin size={11} style={{ opacity: 0.45 }} />
                    <Typography variant="caption" color="text.secondary">{distance}</Typography>
                  </Box>
                )}
                {duration && (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.3 }}>
                    <Clock size={11} style={{ opacity: 0.45 }} />
                    <Typography variant="caption" color="text.secondary">{duration}</Typography>
                  </Box>
                )}
              </Box>
            )}
            {proximityLabel && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.3 }}>
                <LocateFixed size={11} style={{ opacity: 0.55 }} />
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                  {proximityLabel} luota
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      {/* ── Time bar ── */}
      <Box
        sx={{
          px: { xs: 1.5, sm: 2 }, py: 1,
          bgcolor: "action.hover",
          borderTop: "1px solid", borderBottom: "1px solid", borderColor: "divider",
          display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1,
        }}
      >
        {isCompleted ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <CircleCheck size={13} color="green" />
            <Typography variant="caption" color="text.secondary" fontWeight={600}>Valmistui</Typography>
            <Typography variant="caption" color="text.secondary">{formatFi(completedDate)}</Typography>
          </Box>
        ) : (
          <>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.35 }}>
              <TimeRow label="Nouto"    from={pickUpDate}               to={pickupTo} />
              <TimeRow label="Toimitus" from={targetFrom || targetDate} to={targetFrom ? targetDate : null} />
            </Box>

            {/* Transport badge + urgency chip */}
            <Box sx={{ display: "flex", flexDirection:'column', alignItems: "center", gap: 0.75, flexShrink: 0 }}>
              <DeliveryTypeBadge
                deliveryType={delivery?.deliveryType}
                deliveryTransportType={delivery?.deliveryTransportType}
              />
              {timeLeftLabel && (
                <Chip
                  label={timeLeftLabel}
                  size="small"
                  color={chipStyle.color !== "default" ? chipStyle.color : undefined}
                  variant={chipStyle.variant}
                  icon={<Timer size={11} />}
                  sx={{
                    height: 22, fontSize: "0.72rem", fontWeight: 700, flexShrink: 0,
                    "& .MuiChip-icon": { ml: "5px" },
                    "& .MuiChip-label": { px: 0.75 },
                  }}
                />
              )}
            </Box>
          </>
        )}
      </Box>

      {/* ── Action bar ── */}
      <Box
        sx={{ px: { xs: 1.5, sm: 2 }, py: 1, display: "flex", alignItems: "center", gap: 1 }}
        onClick={(e) => e.stopPropagation()}
      >
        {isActive && (
          <>
            <Button
              variant="contained"
              disableElevation
              fullWidth
              onClick={(e) => { e.stopPropagation(); onAccept(); }}
              sx={{ fontWeight: 700, py: 0.75 }}
            >
              Hyväksy kuljetus
            </Button>
            <ShareButton delivery={delivery} />
          </>
        )}

        {isOngoing && (
          <Button
            variant="outlined"
            fullWidth
            onClick={(e) => { e.stopPropagation(); onOpen(); }}
            sx={{ fontWeight: 700, py: 0.75 }}
          >
            Avaa kuljetus
          </Button>
        )}

        {isCompleted && !delivery.reviewed && (
          <QuickReview delivery={delivery} onReview={onReview} />
        )}

        {isCompleted && delivery.reviewed && (
          <StarRow stars={delivery.stars ?? 5} />
        )}

        {isOngoing && (
          <Tooltip title={expanded ? "Piilota tiedot" : "Näytä lisätiedot"}>
            <IconButton size="small" onClick={(e) => { e.stopPropagation(); setExpanded((v) => !v); }}>
              {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* ── Expanded details (ongoing only) ── */}
      {isOngoing && (
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <Divider />
          <Box
            sx={{
              px: { xs: 1.5, sm: 2 }, py: 1.5,
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr 1fr" },
              gap: 2,
            }}
          >
            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight={700}
                sx={{ textTransform: "uppercase", letterSpacing: 0.5, display: "block", mb: 0.5 }}>
                Nouto
              </Typography>
              <Typography variant="body2">{origin}</Typography>
              {delivery?.originInfo?.details?.instructions && (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
                  {delivery.originInfo.details.instructions}
                </Typography>
              )}
              {delivery?.senderInfo?.name && (
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  {delivery.senderInfo.name}
                  {delivery.senderPhone ? ` · ${delivery.senderPhone}` : ""}
                </Typography>
              )}
            </Box>

            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight={700}
                sx={{ textTransform: "uppercase", letterSpacing: 0.5, display: "block", mb: 0.5 }}>
                Toimitus
              </Typography>
              <Typography variant="body2">{destination}</Typography>
              {delivery?.destinationInfo?.details?.instructions && (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
                  {delivery.destinationInfo.details.instructions}
                </Typography>
              )}
              {delivery?.receiverInfo?.name && (
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  {delivery.receiverInfo.name}
                  {delivery.receiverPhone ? ` · ${delivery.receiverPhone}` : ""}
                </Typography>
              )}
            </Box>

            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight={700}
                sx={{ textTransform: "uppercase", letterSpacing: 0.5, display: "block", mb: 0.5 }}>
                Paketti
              </Typography>
              {delivery?.packageSize && (
                <Typography variant="body2">
                  {delivery.packageSize}
                  {delivery.weightKg ? ` · ${delivery.weightKg} kg` : ""}
                </Typography>
              )}
              {delivery?.deliveryTransportType && (
                <Typography variant="caption" color="text.secondary" display="block">
                  {delivery.deliveryTransportType}
                </Typography>
              )}
            </Box>
          </Box>
        </Collapse>
      )}
    </Paper>
  );
}